import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  BellRing, CalendarClock, Check, Loader2, Plus, Trash2, X, AlarmClock,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { leadsAPI } from '@/lib/api';
import DateTimeField from '@/components/common/DateTimeField';

// Phase 27.28 Fase A — Follow-up card embedded on the lead detail
// page. Lets the assigned agent schedule, edit, complete, or cancel
// follow-up reminders. A yellow "Due soon" badge lights up 1 hour
// before the scheduled time. The backend scheduler will send in-app +
// email reminders in the same 1-hour window.

const HOUR_MS = 60 * 60 * 1000;

function toLocalDatetimeInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toIsoUtc(local) {
  if (!local) return null;
  return new Date(local).toISOString();
}

function fmt(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

// Pill that surfaces the Google Calendar sync outcome for a follow-up.
// Silent (renders nothing) when there's no status — e.g. very old rows
// created before the sync mechanism existed. Otherwise it explains why
// the follow-up did NOT land in Google (agent not linked to a portal
// user, agent didn't connect Google, or a transient API error).
function GoogleSyncPill({ status }) {
  if (!status) return null;
  const map = {
    synced: { cls: 'bg-emerald-100 text-emerald-800 border-emerald-300', label: 'Google ✓', title: 'Synced to your Google Calendar.' },
    pending: { cls: 'bg-slate-100 text-slate-600 border-slate-300', label: 'Google …', title: 'Sync in progress — refresh in a few seconds.' },
    user_not_connected: {
      cls: 'bg-amber-100 text-amber-800 border-amber-300',
      label: 'Connect Google',
      title: 'Not synced — connect your Google Calendar from Settings → Integrations → Google Calendar.',
    },
    no_linked_user: {
      cls: 'bg-slate-100 text-slate-600 border-slate-300',
      label: 'No portal user',
      title: 'The assigned agent is not linked to a portal login yet.',
    },
    api_error: { cls: 'bg-rose-100 text-rose-700 border-rose-300', label: 'Google !', title: 'Google API rejected the event. Reconnect and try again.' },
    error: { cls: 'bg-rose-100 text-rose-700 border-rose-300', label: 'Google !', title: 'Sync failed — see admin logs.' },
  };
  const meta = map[status] || { cls: 'bg-slate-100 text-slate-600 border-slate-300', label: status, title: status };
  return (
    <Badge className={`text-[10px] font-normal border ${meta.cls}`} title={meta.title} data-testid="followup-google-sync-pill">
      {meta.label}
    </Badge>
  );
}

export default function LeadFollowUpCard({ leadId, onChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ scheduled_at: '', note: '' });
  const [editing, setEditing] = useState(null); // follow_up_id being edited
  const [editForm, setEditForm] = useState({ scheduled_at: '', note: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await leadsAPI.listFollowUps(leadId);
      setItems(res.data.follow_ups || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.scheduled_at) { toast.error('Pick a date/time'); return; }
    setCreating(true);
    try {
      await leadsAPI.createFollowUp(leadId, toIsoUtc(form.scheduled_at), form.note || null);
      toast.success('Follow-up scheduled');
      setForm({ scheduled_at: '', note: '' });
      load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to schedule');
    } finally { setCreating(false); }
  };

  const complete = async (fid) => {
    try {
      await leadsAPI.completeFollowUp(fid);
      toast.success('Marked done');
      load();
      onChange?.();
    } catch { toast.error('Failed'); }
  };

  const cancel = async (fid) => {
    if (!window.confirm('Cancel this follow-up?')) return;
    try {
      await leadsAPI.cancelFollowUp(fid);
      toast.success('Follow-up cancelled');
      load();
      onChange?.();
    } catch { toast.error('Failed'); }
  };

  const startEdit = (item) => {
    setEditing(item.follow_up_id);
    setEditForm({ scheduled_at: toLocalDatetimeInput(item.scheduled_at), note: item.note || '' });
  };

  const saveEdit = async () => {
    try {
      await leadsAPI.updateFollowUp(editing, {
        scheduled_at: toIsoUtc(editForm.scheduled_at),
        note: editForm.note || null,
      });
      toast.success('Follow-up updated');
      setEditing(null);
      load();
      onChange?.();
    } catch { toast.error('Update failed'); }
  };

  const pending = items.filter((i) => !i.done);
  const past = items.filter((i) => i.done);

  return (
    <Card data-testid="lead-followup-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <BellRing size={14} className="text-amber-500" /> Follow-ups
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Composer */}
        <div className="border border-slate-200 rounded-md p-3 bg-slate-50 space-y-2">
          <p className="text-xs font-medium text-slate-600">Schedule a follow-up</p>
          <div className="grid grid-cols-1 gap-2">
            <div>
              <Label className="text-xs">When</Label>
              <DateTimeField
                value={form.scheduled_at}
                onChange={(v) => setForm({ ...form, scheduled_at: v })}
                testId="followup-datetime-input"
              />
            </div>
            <div>
              <Label className="text-xs">Note (optional)</Label>
              <Textarea
                rows={2}
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder="What to do / talk about"
                className="bg-white text-sm"
                data-testid="followup-note-input"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button size="sm" onClick={create} disabled={creating || !form.scheduled_at} data-testid="followup-create-btn">
              {creating ? <Loader2 className="animate-spin mr-1" size={12} /> : <Plus size={12} className="mr-1" />}
              Schedule
            </Button>
          </div>
        </div>

        {/* Active */}
        {loading ? (
          <div className="text-slate-500 text-sm flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : pending.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-2" data-testid="followup-empty">
            No pending follow-ups.
          </p>
        ) : (
          <div className="space-y-2">
            {pending.map((item) => {
              const scheduledMs = new Date(item.scheduled_at).getTime();
              const now = Date.now();
              const isDueSoon = scheduledMs - now <= HOUR_MS && scheduledMs > now;
              const isOverdue = scheduledMs <= now;
              const isEditing = editing === item.follow_up_id;
              return (
                <div key={item.follow_up_id} className={`border rounded p-2 ${isOverdue ? 'border-rose-300 bg-rose-50' : isDueSoon ? 'border-amber-300 bg-amber-50' : 'border-slate-200'}`} data-testid={`followup-item-${item.follow_up_id}`}>
                  {isEditing ? (
                    <div className="space-y-2">
                      <DateTimeField value={editForm.scheduled_at} onChange={(v) => setEditForm({ ...editForm, scheduled_at: v })} />
                      <Textarea rows={2} value={editForm.note} onChange={(e) => setEditForm({ ...editForm, note: e.target.value })} />
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => setEditing(null)}><X size={12} /></Button>
                        <Button size="sm" onClick={saveEdit} data-testid={`followup-save-${item.follow_up_id}`}>Save</Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <CalendarClock size={12} className="text-slate-500" />
                            <span className="text-sm font-medium">{fmt(item.scheduled_at)}</span>
                            {isOverdue && <Badge className="bg-rose-100 text-rose-700 border-rose-300 border text-[10px] font-normal">OVERDUE</Badge>}
                            {isDueSoon && <Badge className="bg-amber-100 text-amber-800 border-amber-300 border text-[10px] font-normal">DUE SOON</Badge>}
                            {item.reminder_sent_at && <Badge className="bg-blue-100 text-blue-700 border-blue-300 border text-[10px] font-normal"><AlarmClock size={9} className="mr-0.5" />Reminded</Badge>}
                            <GoogleSyncPill status={item.google_sync_status} />
                          </div>
                          {item.note && <p className="text-xs text-slate-600 mt-1 whitespace-pre-wrap">{item.note}</p>}
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => startEdit(item)} title="Edit">
                            <span className="text-xs">✎</span>
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-emerald-600" onClick={() => complete(item.follow_up_id)} title="Mark done" data-testid={`followup-done-${item.follow_up_id}`}>
                            <Check size={12} />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-500" onClick={() => cancel(item.follow_up_id)} title="Cancel" data-testid={`followup-cancel-${item.follow_up_id}`}>
                            <Trash2 size={12} />
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Past */}
        {past.length > 0 && (
          <details className="text-xs">
            <summary className="cursor-pointer text-slate-500">Show {past.length} completed</summary>
            <div className="mt-2 space-y-1">
              {past.map((item) => (
                <div key={item.follow_up_id} className="text-xs text-slate-500 border-l-2 border-emerald-200 pl-2">
                  <span className="line-through">{fmt(item.scheduled_at)}</span>
                  {item.note && <p className="italic">{item.note}</p>}
                </div>
              ))}
            </div>
          </details>
        )}
      </CardContent>
    </Card>
  );
}
